import {classes, present} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {FieldText} from '@bitstillery/common/components/fields/text/text'
import {modelref_adapter, modelref_assign} from '@bitstillery/common/lib/store'
import m from '@bitstillery/mithril'

import {FieldCurrency} from '../currency/currency'

import type {FieldSelectAttrs, FieldTextAttrs} from '@bitstillery/common/types/field'

interface FieldMoneyAttrs extends Omit<FieldTextAttrs, 'min' | 'step'> {
    additional_class?: string
    change_currency?: boolean
    /** A model to pick the currency with, or just the currency code to show beside the amount. */
    currency?: FieldSelectAttrs['model'] | string
    icon?: string
    /** Its presence (`in`) puts the floor on arrow-key steps, so it is left out rather than undefined. */
    min?: number
    tabindex?: number
    precision?: number // Default: 2
    show_addon?: boolean
    /** The arrow keys' step; CMS JSX reaches FieldMoney through the catalog untyped, so it can be a string attribute. */
    step?: number | string
    value?: string
}

export class FieldMoney extends MithrilComponent<FieldMoneyAttrs> {
    precision: number = 2

    override oninit(vnode: m.Vnode<FieldMoneyAttrs>) {
        this.precision = vnode.attrs.precision ?? this.precision
        const {model_value, model_ref} = modelref_adapter(vnode.attrs.model)
        // Make sure a set model value always starts with two decimals; e.g.
        // 144.00 instead of 144 & 144.20 instead of 144.2
        if (model_value) {
            modelref_assign(model_ref, Number(model_value).toFixed(this.precision))
        }
    }

    format_value(value: string, from_navigation = false) {
        if (value === '') {
            return ''
        }
        // A comma becomes a decimal point
        value = value.replace(',', '.')
        // Non-numeric chars except decimal point and minus sign are removed
        value = value.replaceAll(/[^\d.-]/g, '')
        // More than 1 points are not allowed
        const parts = value.split('.')
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('')
        }
        // Only a minus sign at the start is allowed
        if (value.indexOf('-') > 0) {
            value = value.replaceAll('-', '')
        } else if (value.split('-').length > 2) {
            value = '-' + value.replaceAll('-', '')
        }

        // More than 'this.precision' decimal places are not allowed (e.g. 54.546)
        if (value.includes('.') && present(value.split('.')[1], 'decimals').length > this.precision) {
            value = Number(value).toFixed(this.precision)
        }

        if (from_navigation) {
            // If there's exactly one decimal, add a trailing zero
            if (value.includes('.') && present(value.split('.')[1], 'decimals').length === 1) {
                value += '0'
            }
        }

        return value || null
    }

    view(vnode: m.Vnode<FieldMoneyAttrs>): m.Children {
        const {validation} = vnode.attrs
        if (validation && vnode.attrs.label) {
            validation.description = vnode.attrs.label
        }
        const invalid = validation ? validation._invalid : false

        return (
            <div
                className={classes('c-field-composed-money', 'field', 'no-click', vnode.attrs.className, {
                    currency: vnode.attrs.currency,
                    disabled: vnode.attrs.disabled,
                    invalid: invalid && validation?.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}
                <div className='control'>
                    <FieldText
                        autofocus={vnode.attrs.autofocus}
                        composed={true}
                        formatter={(value) => this.format_value(value)}
                        help={vnode.attrs.help}
                        max={vnode.attrs.max}
                        min={vnode.attrs.min}
                        model={vnode.attrs.model}
                        onkeydown={(e) => {
                            const {model_value, model_ref} = modelref_adapter(vnode.attrs.model)
                            let step_size = 0.05
                            if (vnode.attrs.step && !isNaN(Number(vnode.attrs.step))) {
                                step_size = Number(vnode.attrs.step)
                            } else {
                                if (e.ctrlKey) {
                                    if (e.shiftKey) {
                                        step_size = 10
                                    } else {
                                        step_size = 0.1
                                    }
                                } else if (e.shiftKey) {
                                    step_size = 1
                                }
                            }

                            if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
                                e.preventDefault()
                                const delta = e.key === 'ArrowDown' ? -step_size : step_size
                                const new_value = this.format_value(
                                    String(Number.parseFloat((Number(model_value) + delta).toFixed(this.precision))),
                                    true,
                                )
                                // A model value that isn't a number formats to nothing; stepping from it has no meaning.
                                if (new_value === null) {
                                    return
                                }

                                if ('min' in vnode.attrs) {
                                    if (Number(new_value) >= vnode.attrs.min) {
                                        modelref_assign(model_ref, new_value)
                                        if (vnode.attrs.oninput) {
                                            vnode.attrs.oninput(new_value)
                                        }
                                    }
                                } else {
                                    modelref_assign(model_ref, new_value)
                                    if (vnode.attrs.oninput) {
                                        vnode.attrs.oninput(new_value)
                                    }
                                }
                            }
                        }}
                        oninput={(value) => {
                            if (vnode.attrs.oninput) {
                                vnode.attrs.oninput(value)
                            }
                        }}
                        placeholder={vnode.attrs.placeholder}
                        tabindex={vnode.attrs.tabindex}
                        validation={validation}
                    />
                    {(() => {
                        const currency = vnode.attrs.currency
                        if (!currency) {
                            return null
                        }
                        if (typeof currency === 'string' || !vnode.attrs.change_currency) {
                            return <span class='control-addon'>{modelref_adapter(currency).model_value}</span>
                        }

                        return (
                            <FieldCurrency
                                className='change-currency'
                                composed={true}
                                disabled={vnode.attrs.disabled}
                                model={currency}
                            />
                        )
                    })()}
                </div>
            </div>
        )
    }
}
